import type { CSSProperties, PointerEvent } from 'react'
import type { SectionRow, SettingsRow } from '@/types'

import { useRef, useState } from 'react'
import { burstConfetti, fireworks, heartShower } from '@/lib/celebrate'

import {
    Gem,
    Gift,
    Heart,
    Phone,
    RotateCcw
} from 'lucide-react'

import FloatingHearts from '@/components/landing/floating-hearts'
import Reveal from '@/components/landing/reveal'

type Stage = 'asking' | 'yes' | 'gift'

const TEASES = [
    'Pakka?',
    'Soch lo ek baar...',
    'Galat button!',
    'Ye button kaam nahi karta',
    'Bas Haan hi bacha hai'
]

/* Seeded so the server and the browser place every star in the same spot. */
const seeded = (seed: number) => () => {
    seed = (seed + 0x6d2b79f5) | 0

    let value = seed

    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
}

const random = seeded(20261105)

const STARS = Array.from({ length: 44 }, () => {
    const size = random()

    return {
        left: (random() * 100).toFixed(2),
        top: (random() * 100).toFixed(2),
        size: size > 0.86 ? 3 : size > 0.5 ? 2 : 1,
        duration: (2.2 + random() * 3).toFixed(2),
        delay: (random() * 4).toFixed(2)
    }
})

interface FinaleSectionProps {
    section?: SectionRow
    settings: SettingsRow | null
    onReplay: () => void
}

export default function FinaleSection({ section, settings, onReplay }: FinaleSectionProps) {
    const [stage, setStage] = useState<Stage>('asking')
    const [dodges, setDodges] = useState(0)
    const [noSpot, setNoSpot] = useState<{ x: number, y: number } | null>(null)
    const arenaRef = useRef<HTMLDivElement>(null)
    const noRef = useRef<HTMLButtonElement>(null)
    const yesRef = useRef<HTMLButtonElement>(null)

    const dodge = () => {
        const arena = arenaRef.current
        const button = noRef.current

        if (!arena || !button) return

        const width = Math.max(button.offsetWidth, 230)
        const maxX = Math.max(0, arena.clientWidth - width)
        const maxY = Math.max(0, arena.clientHeight - button.offsetHeight)
        const arenaBox = arena.getBoundingClientRect()
        const yesBox = yesRef.current?.getBoundingClientRect()
        let spot = { x: Math.random() * maxX, y: Math.random() * maxY }

        for (let attempt = 0; attempt < 12 && yesBox; attempt += 1) {
            const left = arenaBox.left + spot.x
            const top = arenaBox.top + spot.y
            const overlaps = left < yesBox.right + 12
                && left + width > yesBox.left - 12
                && top < yesBox.bottom + 12
                && top + button.offsetHeight > yesBox.top - 12

            if (!overlaps) break

            spot = { x: Math.random() * maxX, y: Math.random() * maxY }
        }

        setNoSpot(spot)
        setDodges(value => value + 1)
    }

    const onNoPointerEnter = (event: PointerEvent<HTMLButtonElement>) => {
        if (event.pointerType === 'mouse') dodge()
    }

    const onNoPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault()
        dodge()
    }

    const sayYes = () => {
        setStage('yes')
        void fireworks(6500)
        void heartShower()
    }

    const openGift = () => {
        setStage('gift')
        void burstConfetti()
    }

    const replay = () => {
        setStage('asking')
        setDodges(0)
        setNoSpot(null)
        onReplay()
    }

    const yesScale = Math.min(1 + dodges * 0.08, 1.5)

    return (
        <section id='finale' className='relative isolate flex min-h-svh items-center overflow-hidden bg-night py-24 text-cream sm:py-32'>
            <div aria-hidden className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,#4a1336_0%,#2e1028_40%,#1a0916_100%)]' />

            <div aria-hidden className='absolute inset-0 -z-10'>
                {STARS.map((star, index) => (
                    <span
                        key={index}
                        className='absolute animate-twinkle rounded-full bg-white motion-reduce:animate-none'
                        style={{
                            'left': `${star.left}%`,
                            'top': `${star.top}%`,
                            'width': star.size,
                            'height': star.size,
                            '--star-duration': `${star.duration}s`,
                            '--star-delay': `-${star.delay}s`
                        } as CSSProperties}
                    />
                ))}
            </div>

            <FloatingHearts className='-z-10' count={8} />

            <div className='mx-auto w-full max-w-3xl px-6 text-center'>
                {stage === 'asking' && (
                    <Reveal direction='zoom'>
                        <span className='mx-auto grid size-20 animate-heartbeat place-items-center rounded-full bg-gradient-to-br from-[#ffe3ea] to-[#ffb3c6] text-petal-deep shadow-[0_0_60px_10px_rgba(232,84,124,0.35)] motion-reduce:animate-none'>
                            <Gem aria-hidden className='size-9' />
                        </span>

                        {section?.kicker && (
                            <p className='mt-8 text-xs font-bold tracking-[0.32em] text-[#ff9ab4] uppercase'>
                                <span data-mdlfy-cms={`sections:${section.id}:kicker`}>
                                    {section.kicker}
                                </span>
                            </p>
                        )}

                        {section?.title && (
                            <h2
                                data-mdlfy-cms={`sections:${section.id}:title`}
                                className='mt-4 font-heading text-5xl leading-[1.05] font-semibold text-balance text-cream sm:text-6xl md:text-7xl'
                            >
                                {section.title}
                            </h2>
                        )}

                        {section?.subtitle && (
                            <p
                                data-mdlfy-cms={`sections:${section.id}:subtitle`}
                                className='mx-auto mt-5 max-w-md text-lg text-pretty text-cream/75'
                            >
                                {section.subtitle}
                            </p>
                        )}

                        <div ref={arenaRef} className='relative mx-auto mt-12 h-56 w-full max-w-xl sm:h-44'>
                            <div className='flex h-full items-center justify-center gap-5'>
                                <button
                                    ref={yesRef}
                                    type='button'
                                    onClick={sayYes}
                                    className='relative z-10 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-petal to-petal-deep px-8 py-4 text-lg font-bold text-white shadow-[0_16px_50px_-12px_rgba(232,84,124,0.95)] transition-transform duration-300 outline-none hover:brightness-110 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60'
                                    style={{ transform: `scale(${yesScale})` }}
                                >
                                    <Heart aria-hidden className='size-5 fill-current' />

                                    {settings?.finale_yes
                                        ? (
                                            <span data-mdlfy-cms={`settings:${settings.id}:finale_yes`}>
                                                {settings.finale_yes}
                                            </span>
                                        )
                                        : <span>Haan</span>}
                                </button>

                                {!noSpot && (
                                    <button
                                        ref={noRef}
                                        type='button'
                                        onClick={dodge}
                                        onPointerEnter={onNoPointerEnter}
                                        onPointerDown={onNoPointerDown}
                                        className='rounded-full bg-white/10 px-7 py-4 text-lg font-bold text-cream/85 ring-1 ring-white/20 backdrop-blur outline-none focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                                    >
                                        {settings?.finale_no
                                            ? (
                                                <span data-mdlfy-cms={`settings:${settings.id}:finale_no`}>
                                                    {settings.finale_no}
                                                </span>
                                            )
                                            : <span>Nahi</span>}
                                    </button>
                                )}
                            </div>

                            {noSpot && (
                                <button
                                    ref={noRef}
                                    type='button'
                                    onClick={dodge}
                                    onPointerEnter={onNoPointerEnter}
                                    onPointerDown={onNoPointerDown}
                                    className='absolute top-0 left-0 rounded-full bg-white/10 px-6 py-3 text-base font-bold whitespace-nowrap text-cream/85 ring-1 ring-white/20 backdrop-blur transition-[translate] duration-300 ease-out outline-none focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                                    style={{ translate: `${noSpot.x}px ${noSpot.y}px` }}
                                >
                                    {TEASES[(dodges - 1) % TEASES.length]}
                                </button>
                            )}
                        </div>
                    </Reveal>
                )}

                {stage !== 'asking' && (
                    <div className='animate-in duration-1000 fade-in zoom-in-90'>
                        {settings?.finale_after_title && (
                            <h2
                                data-mdlfy-cms={`settings:${settings.id}:finale_after_title`}
                                className='font-script text-7xl leading-none text-[#ffd1dc] text-shadow-[0_0_30px_rgba(232,84,124,0.6)] sm:text-8xl'
                            >
                                {settings.finale_after_title}
                            </h2>
                        )}

                        {settings?.finale_after_subtitle && (
                            <p
                                data-mdlfy-cms={`settings:${settings.id}:finale_after_subtitle`}
                                className='mx-auto mt-6 max-w-lg font-heading text-2xl leading-snug text-balance text-cream/90 italic'
                            >
                                {settings.finale_after_subtitle}
                            </p>
                        )}

                        {stage === 'yes' && (
                            <button
                                type='button'
                                onClick={openGift}
                                className='group mx-auto mt-12 flex flex-col items-center gap-5 rounded-3xl p-3 outline-none focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                            >
                                <span aria-hidden className='relative block size-40 animate-wiggle motion-reduce:animate-none'>
                                    <span className='absolute inset-x-3 top-14 bottom-0 rounded-b-2xl bg-gradient-to-b from-petal to-petal-deep shadow-[0_25px_50px_-20px_rgba(0,0,0,0.8)]' />
                                    <span className='absolute top-14 bottom-0 left-1/2 w-6 -translate-x-1/2 bg-gold' />

                                    <span className='absolute inset-x-0 top-8 h-9 rounded-xl bg-gradient-to-b from-[#ff8fae] to-petal shadow-lg transition-transform duration-300 group-hover:-translate-y-1.5 group-hover:-rotate-3'>
                                        <span className='absolute inset-y-0 left-1/2 w-6 -translate-x-1/2 bg-gold' />
                                        <span className='absolute -top-7 left-1/2 size-9 -translate-x-[95%] -rotate-[30deg] rounded-full border-[6px] border-gold' />
                                        <span className='absolute -top-7 left-1/2 size-9 -translate-x-[5%] rotate-[30deg] rounded-full border-[6px] border-gold' />
                                    </span>
                                </span>

                                <span className='inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 font-bold text-wine shadow-lg transition group-hover:bg-white'>
                                    <Gift aria-hidden className='size-5 text-petal-deep' />

                                    {settings?.gift_cta
                                        ? (
                                            <span data-mdlfy-cms={`settings:${settings.id}:gift_cta`}>
                                                {settings.gift_cta}
                                            </span>
                                        )
                                        : <span>Gift kholo</span>}
                                </span>
                            </button>
                        )}

                        {stage === 'gift' && (
                            <div className='mx-auto mt-12 max-w-lg animate-in duration-700 fade-in slide-in-from-bottom-6'>
                                <div className='letter-paper relative rounded-2xl px-7 pt-12 pb-8 text-left text-ink shadow-[0_30px_80px_-30px_rgba(232,84,124,0.7)] sm:px-10'>
                                    <span aria-hidden className='absolute -top-7 left-1/2 grid size-14 -translate-x-1/2 place-items-center rounded-full bg-gradient-to-br from-gold to-[#e8a94f] text-white shadow-lg ring-4 ring-night'>
                                        <Gift className='size-6' />
                                    </span>

                                    {settings?.gift_message && (
                                        <p
                                            data-mdlfy-cms={`settings:${settings.id}:gift_message`}
                                            className='pl-6 font-hand text-[1.6rem] leading-9 text-ink sm:pl-4 sm:text-3xl'
                                        >
                                            {settings.gift_message}
                                        </p>
                                    )}

                                    <p className='mt-6 flex items-center justify-end gap-2 text-petal-deep'>
                                        <Phone aria-hidden className='size-5 animate-wiggle motion-reduce:animate-none' />
                                        <Heart aria-hidden className='size-5 fill-current' />
                                    </p>
                                </div>

                                <button
                                    type='button'
                                    onClick={replay}
                                    className='mt-10 inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-bold text-cream ring-1 ring-white/20 transition outline-none hover:bg-white/20 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                                >
                                    <RotateCcw aria-hidden className='size-4' />
                                    Surprise phir se dekho
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    )
}
